import { redirect } from "@tanstack/react-router";
import { useAuthStore, type UserRole } from "@/store/authStore";

const PORTAL_HOME = {
  admin: "/admin/dashboard",
  partner: "/partner/dashboard",
  student: "/student/dashboard",
  unassigned: "/onboarding",
} as const satisfies Record<UserRole, string>;

export function portalHomeFor(role: UserRole): string {
  return PORTAL_HOME[role] ?? "/auth/login";
}

/**
 * Client-side role gate for a portal layout route.
 *
 * This mirrors RoleMiddleware on the API so a user who lands in another role's
 * section is sent to their own portal instead of mounting a page whose every
 * request will 403. It is a usability redirect, not a security control: the API
 * remains the only thing that actually authorizes a request.
 */
export async function requireRole(allowed: readonly UserRole[]) {
  const store = useAuthStore.getState();
  // beforeLoad runs before the root component's checkSession effect, so on a
  // hard refresh the store is still empty. Awaiting hydration keeps a valid
  // session from being bounced to the login page on every reload.
  if (!store.isHydrated) {
    await store.checkSession();
  }

  const { user } = useAuthStore.getState();
  if (!user) {
    throw redirect({ to: "/auth/login" });
  }
  if (!allowed.includes(user.role)) {
    throw redirect({ to: portalHomeFor(user.role) });
  }
}
